"use client";

import Image from "next/image";
import { useEffect } from "react";
import { createClientLogger, serializeError } from "@/lib/client-logger";

const log = createClientLogger("global-error-boundary");

export default function GlobalError({
  error,
  reset
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    if (error) {
      log.error("Fatal error caught by global error boundary", {
        error: serializeError(error),
        ...(error.digest ? { digest: error.digest } : {})
      });
    }
  }, [error]);

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>Error - Shield</title>
        <style
          dangerouslySetInnerHTML={{
            __html: `
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            html, body {
              width: 100%;
              height: 100%;
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
              -webkit-font-smoothing: antialiased;
              -moz-osx-font-smoothing: grayscale;
            }
            body {
              background: radial-gradient(circle at center, #E8F2FF 0%, #DCEAFF 35%, #C8DDFC 100%);
              display: flex;
              min-height: 100vh;
              align-items: center;
              justify-content: center;
              padding: 2rem 1rem;
            }
            .error-wrapper {
              width: 100%;
              max-width: 80rem;
              background: rgba(255, 255, 255, 0.95);
              border-radius: 1.5rem;
              padding: 3rem 2rem;
              box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
              text-align: center;
            }
            .logo-container {
              display: flex;
              justify-content: center;
              margin-bottom: 2rem;
            }
            .logo-container img {
              height: 60px;
              width: auto;
            }
            h1 {
              font-size: 2.5rem;
              font-weight: 600;
              color: #0f172a;
              margin-bottom: 1rem;
            }
            h2 {
              font-size: 1.5rem;
              font-weight: 500;
              color: #1e293b;
              margin-bottom: 1.5rem;
            }
            p {
              font-size: 1.125rem;
              color: #475569;
              margin-bottom: 2rem;
              max-width: 36rem;
              margin-left: auto;
              margin-right: auto;
            }
            .button-group {
              display: flex;
              gap: 1rem;
              justify-content: center;
              flex-wrap: wrap;
              margin-bottom: 2rem;
            }
            button {
              min-width: 180px;
              padding: 0.875rem 1.75rem;
              border-radius: 0.5rem;
              font-weight: 500;
              font-size: 1rem;
              cursor: pointer;
              border: none;
              transition: all 0.2s;
            }
            .btn-primary {
              background: #2563eb;
              color: white;
            }
            .btn-primary:hover {
              background: #1d4ed8;
            }
            .btn-secondary {
              background: white;
              color: #334155;
              border: 1px solid #bfdbfe;
            }
            .btn-secondary:hover {
              background: #eff6ff;
            }
            .error-id {
              margin-top: 2rem;
              font-size: 0.875rem;
              color: #64748b;
            }
            .help-section {
              margin-top: 2.5rem;
              padding: 1.5rem;
              background: rgba(255, 255, 255, 0.7);
              border-radius: 1rem;
              border: 1px solid #bfdbfe;
            }
            .help-section h3 {
              font-size: 1.125rem;
              font-weight: 600;
              color: #0f172a;
              margin-bottom: 0.5rem;
            }
            .help-section p {
              font-size: 0.875rem;
              color: #475569;
              margin-bottom: 1rem;
            }
            .help-links {
              display: flex;
              gap: 0.75rem;
              justify-content: center;
              flex-wrap: wrap;
            }
            .help-links a {
              padding: 0.5rem 1rem;
              border-radius: 0.375rem;
              text-decoration: none;
              font-size: 0.875rem;
              transition: all 0.2s;
            }
            .help-links a:first-child {
              border: 1px solid #93c5fd;
              color: #1e40af;
            }
            .help-links a:first-child:hover {
              background: #dbeafe;
            }
            .help-links a:last-child {
              color: #475569;
            }
            .help-links a:last-child:hover {
              background: #f1f5f9;
            }
          `
          }}
        />
      </head>
      <body>
        <div className="error-wrapper">
          <div className="logo-container">
            <Image src="/images/shieldLogo.svg" alt="Shield Logo" />
          </div>
          <h2>We&apos;ll get you back to safety</h2>
          <p>
            We encountered an unexpected error. Don&apos;t worry, our team has
            been notified and we&apos;re working to fix it.
          </p>

          <div className="button-group">
            <button
              className="btn-primary"
              onClick={() => {
                reset();
              }}
            >
              🔄 Try Again
            </button>
            <button
              className="btn-secondary"
              onClick={() => {
                window.location.assign("/");
              }}
            >
              🏠 Go to Dashboard
            </button>
          </div>

          <div className="help-section">
            <h3>Need help right now?</h3>
            <p>
              Reach out to our support crew and we&apos;ll take it from here.
            </p>
            <div className="help-links">
              <a href="mailto:support@getshield.xyz">Contact Support</a>
              <a href="/help">Help Center</a>
            </div>
          </div>

          {error?.digest && (
            <div className="error-id">Error ID: {error.digest}</div>
          )}
        </div>
      </body>
    </html>
  );
}
